import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebounce } from 'react-use';
import {
    searchAutocomplete as searchAutocompleteApi,
    trendingSearches as trendingSearchesApi,
} from '../api';
import { CompletionType } from '../api/searchAutocomplete';
import { useErrorDisplayContext, useLocationContext, useSearchInputContext } from '../context';
import { useIntl } from 'react-intl';

export const useSearchAutocompleteQuery = ({
    completionTypes,
}: { completionTypes?: CompletionType[] } = {}) => {
    const { query, onAutocompleteCallback } = useSearchInputContext();
    const { location } = useLocationContext();
    const [debouncedQuery, setDebouncedQuery] = useState(query && query.length >= 1 ? query : '');
    const { setError } = useErrorDisplayContext();
    const { formatMessage } = useIntl();

    useDebounce(
        () => {
            if (query && query.length >= 1 && query !== debouncedQuery) {
                setDebouncedQuery(query);
                onAutocompleteCallback?.();
            }
        },
        400,
        [query],
    );

    const placeId = location?.placeId || '';
    const onlineEvents = location?.isOnline;
    const latitude = location?.latitude || null;
    const longitude = location?.longitude || null;
    const isLocationSet = !!placeId || !!onlineEvents || (!!latitude && !!longitude);

    const queryResponse = useQuery(
        ['search-takeover', debouncedQuery, placeId, latitude, longitude, completionTypes],
        () => {
            return searchAutocompleteApi({
                query: debouncedQuery,
                placeId,
                latitude,
                longitude,
                completionTypes,
            }).catch(() => {
                setError?.({
                    message: formatMessage({
                        id: 'Looks like we had a problem running your search.',
                        defaultMessage: 'Looks like we had a problem running your search.',
                    }),
                    severity: 'error',
                });
            });
        },
        {
            enabled: !!debouncedQuery && isLocationSet,
            keepPreviousData: true,
        },
    );

    return {
        ...queryResponse,
        debouncedQuery,
    };
};

export const useTrendingSearchQuery = () => {
    const { location } = useLocationContext();
    const locationSlug = location?.slug || '';

    return useQuery(
        ['search-takeover', locationSlug],
        () => {
            return trendingSearchesApi({ slug: locationSlug }).catch(() => ({
                trending: {
                    active_timeframe: '',
                    trending_searches: [],
                },
            }));
        },
        { enabled: !!locationSlug },
    );
};
